import React from 'react';
import { Card as CardType } from './types';
import { useMemoryGame } from './useMemoryGame';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Trophy, Timer, ArrowLeft, Star, Zap, XCircle, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Progress } from '@/components/ui/progress';

export const MemoryGame = () => {
  const { gameState, handleCardClick, startGame, selectCategory } = useMemoryGame();
  const navigate = useNavigate();

  // Formatear el tiempo restante
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Componente para el resumen final del juego
  const GameSummary = () => {
    const isComplete = gameState.matchedPairs === gameState.cards.length / 2;
    
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center p-4 bg-white rounded-lg shadow-lg w-full max-w-md mx-auto space-y-4"
      >
        <div className="text-center">
          <h2 className="text-xl font-bold text-primary mb-1">
            {isComplete ? '¡Felicidades! 🎉' : 'Tiempo agotado ⏰'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isComplete ? 'Has encontrado todos los pares' : 'No has podido encontrar todos los pares'}
          </p>
        </div>

        {/* Puntuación y Cerebrix */}
        <div className="bg-primary/5 w-full rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <span className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              <span className="font-medium">Puntos</span>
            </span>
            <span className="text-xl font-bold">{gameState.stats.points}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-500" />
              <span className="font-medium">Cerebrix</span>
            </span>
            <span className="text-xl font-bold">{gameState.cerebrixEarned || 0}</span>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="grid grid-cols-2 gap-3 w-full">
          <div className="bg-primary/5 rounded p-2 text-center">
            <div className="text-xs text-muted-foreground">Pares</div>
            <div className="font-bold">{gameState.matchedPairs}/{gameState.cards.length/2}</div>
          </div>
          <div className="bg-primary/5 rounded p-2 text-center">
            <div className="text-xs text-muted-foreground">Tiempo</div>
            <div className="font-bold">{60 - gameState.timeLeft}s</div>
          </div>
          <div className="bg-primary/5 rounded p-2 text-center">
            <div className="text-xs text-muted-foreground">Racha máx.</div>
            <div className="font-bold">{gameState.stats.maxStreak}</div>
          </div>
          <div className="bg-primary/5 rounded p-2 text-center">
            <div className="text-xs text-muted-foreground">Errores</div>
            <div className="font-bold">{gameState.stats.errors}</div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex gap-3 w-full">
          <Button 
            variant="outline" 
            className="flex-1"
            onClick={() => navigate('/games')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Salir
          </Button>
          <Button 
            variant="default" 
            className="flex-1"
            onClick={() => startGame()}
          >
            <Zap className="h-4 w-4 mr-2" />
            Jugar de nuevo
          </Button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="w-full h-full max-w-2xl mx-auto p-3">
      {/* Header con puntos y tiempo */}
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs font-medium bg-primary/5 px-2 py-1 rounded">
          🎯 Puntos: {gameState.stats.points}
        </div>
        {gameState.gameStarted && (
          <div className="text-xs font-medium bg-primary/5 px-2 py-1 rounded">
            ⏱️ {formatTime(gameState.timeLeft)}
          </div>
        )}
      </div>

      {!gameState.gameStarted && !gameState.isGameOver ? (
        // Pantalla de selección de categoría
        <div className="flex flex-col items-center space-y-4">
          <h2 className="text-lg font-bold text-primary">¡Juego de Memoria!</h2>
          <p className="text-xs text-center text-muted-foreground px-4">
            Elige una categoría y encuentra los pares mientras aprendes
          </p>

          {/* Selector de categoría */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 w-full">
            {[
              { id: 'financial', emoji: '💰', name: 'Finanzas', desc: 'Aprende sobre el dinero', color: 'from-green-100 to-green-50 hover:from-green-400 hover:to-green-500' },
              { id: 'business', emoji: '🏪', name: 'Negocios', desc: 'Sé un emprendedor', color: 'from-blue-100 to-blue-50 hover:from-blue-400 hover:to-blue-500' },
              { id: 'leadership', emoji: '⭐', name: 'Liderazgo', desc: 'Guía a tu equipo', color: 'from-purple-100 to-purple-50 hover:from-purple-400 hover:to-purple-500' },
              { id: 'emotional', emoji: '❤️', name: 'Emociones', desc: 'Conoce tus sentimientos', color: 'from-red-100 to-red-50 hover:from-red-400 hover:to-red-500' }
            ].map(cat => (
              <button
                key={cat.id}
                onClick={() => selectCategory(cat.id as any)}
                className={`
                  aspect-square w-full
                  p-4 rounded-lg 
                  transition-all hover:scale-105 
                  bg-gradient-to-br ${cat.color}
                  hover:text-white
                `}
              >
                <div className="flex flex-col items-center justify-center h-full space-y-2">
                  <span className="text-3xl sm:text-4xl">{cat.emoji}</span>
                  <span className="text-sm sm:text-base font-medium">{cat.name}</span>
                  <span className="text-xs sm:text-sm opacity-75">{cat.desc}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : gameState.isGameOver ? (
        // Pantalla de resumen final
        <GameSummary />
      ) : (
        // Pantalla del juego
        <div className="flex flex-col items-center space-y-3">
          {/* Categoría actual */}
          <div className="text-xs font-medium">
            {gameState.currentCategory === 'financial' && '💰 Finanzas'}
            {gameState.currentCategory === 'business' && '🏪 Negocios'}
            {gameState.currentCategory === 'leadership' && '⭐ Liderazgo'}
            {gameState.currentCategory === 'emotional' && '❤️ Emociones'}
          </div>

          {/* Grid de cartas */}
          <div className="grid grid-cols-4 gap-2 sm:gap-3 w-full">
            {gameState.cards.map((card, index) => (
              <button
                key={card.id}
                onClick={() => handleCardClick(index)}
                className={`
                  relative aspect-[3/4] w-full
                  transition-all duration-300 ease-in-out
                  ${card.isMatched ? 'opacity-60' : ''}
                  ${!card.isFlipped && !card.isMatched ? 'hover:scale-105' : ''}
                `}
              >
                <div className={`
                  absolute inset-0 rounded
                  flex flex-col items-center justify-center
                  ${card.isFlipped ? 'bg-white shadow-sm' : 'bg-gradient-to-br from-primary/10 to-primary/5'}
                  p-2 sm:p-3 text-center
                `}>
                  {card.isFlipped || card.isMatched ? (
                    <>
                      <span className="text-xl sm:text-3xl mb-1 sm:mb-2">{card.emoji}</span>
                      <span className="text-[10px] sm:text-sm font-medium leading-tight">{card.term}</span>
                      <span className="text-[8px] sm:text-xs mt-0.5 sm:mt-1 leading-tight opacity-75">{card.definition}</span>
                    </>
                  ) : (
                    <span className="text-2xl sm:text-4xl opacity-75">❓</span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Estadísticas */}
          <div className="flex items-center justify-center gap-4 text-xs sm:text-sm text-muted-foreground mt-4">
            <span>Pares: {gameState.matchedPairs}</span>
            <span>•</span>
            <span>Racha: {gameState.stats.currentStreak}</span>
            <span>•</span>
            <span>Errores: {gameState.stats.errors}</span>
          </div>
        </div>
      )}
    </div>
  );
};
